"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { TweakOptionSentinel, useTweaks } from "@/components/dev-tools/tweaks2";
import type { TweakControlValueReader } from "@/components/dev-tools/tweaks2";
import { resolveTweakedVariant } from "@/components/dev-tools/tweaksVariantResolution";
// The shared session surface palette: one material family paints the main
// panel (bezel + core + overlay stack), the subproduct cards, and the
// Overview top panel tile.
export enum SessionSurfaceVariant {
  Classic = "Classic",
  Vitrine = "Vitrine",
  Monolith = "Monolith",
  Tray = "Tray",
  AuroraFloor = "Aurora floor",
  Etch = "Etch",
  // The washed family — Classic's colour-washed band/core structure rebuilt
  // as real materials at whisper alphas, so it paints the cards too.
  Mist = "Mist",
  Veil = "Veil",
  Tide = "Tide",
  Fern = "Fern",
  Dusk = "Dusk",
  Smoke = "Smoke",
  // The clear-glass family — subtler still: bright frosted glassmorphism at
  // whisper alphas, geometry drawn by blur and hairlines instead of dark fills.
  Frost = "Frost",
  Quartz = "Quartz",
  // Quartz refinement children — same material, different STACK-FADE
  // behaviour (opacity-fading a backdrop-blurred card kills its blur).
  QuartzContentFade = "Quartz/Content fade",
  // Content-fade grandchildren: content fades, then the backdrop blur drains
  // to 0, and only then the layer itself fades — one reveal motion, so the
  // blur is already gone when element opacity starts.
  QuartzContentFadeDrain = "Quartz/Content fade/Drain",
  QuartzContentFadeDissolve = "Quartz/Content fade/Dissolve",
  QuartzMaskFade = "Quartz/Mask fade",
  QuartzVeil = "Quartz/Veil",
  Breeze = "Breeze",
  Nimbus = "Nimbus",
}

/** Chat message container treatments. */
export enum ChatBubbleVariant {
  /** The shipped HalfGlass bubble. */
  Classic = "Classic",
  /** Pure text block — no background, border, shadow, or padding shell. */
  Naked = "Naked",
  /** No surface; a hairline seam under each message. */
  Seam = "Seam",
  /** Quietline tree parent — spine children draw the line on the message row. */
  Quietline = "Quietline",
  // Quietline spine children — the left line is drawn by the MESSAGE ROW
  // (ChatMessageItem), aligned with the author icon's centre, and the author
  // row is sticky while the message scrolls.
  /** The spine fades out toward the message end. */
  QuietlineFade = "Quietline/Fade",
  // Quietline/Fade refinement children — everything after the sticky author
  // row dissolves via a viewport-anchored mask-image (distinct recipes).
  /** Soft long mask-image ramp under the sticky author row. */
  QuietlineFadeWash = "Quietline/Fade/Wash",
  /** Tight mask-image clip right under the sticky author row. */
  QuietlineFadeClip = "Quietline/Fade/Clip",
  /** Mint spine. */
  QuietlineMint = "Quietline/Mint",
  /** The spine carries the author's member colour. */
  QuietlineMember = "Quietline/Member",
  // Quietline/Member refinement children — the raw member colour at 0.45
  // read garish; these keep the member identity but quiet the paint.
  /** Member colour fading to nothing toward the message end. */
  QuietlineMemberFade = "Quietline/Member/Fade",
  /** Member colour at a whisper opacity — a tinted hairline. */
  QuietlineMemberMuted = "Quietline/Member/Muted",
  /** Sticky author row seated on a frosted glass chip. */
  QuietlineGlass = "Quietline/Glass",
  /** Barely-there white fill, nothing else. */
  Whisper = "Whisper",
  /** Transparent with a hairline ring only. */
  Outline = "Outline",
  // The fresh round (tweak kjio2qp) — new directions after the Mintedge/Slab
  // family was dismissed.
  /** A left-anchored wash that tapers to nothing rightward. */
  Tapered = "Tapered",
  /** Dark frosted glass — blur + hairline, the modal's frost language. */
  Backdrop = "Backdrop",
  /** No fill; hairline seams above AND below, roomy vertical padding. */
  Etched = "Etched",
  /** Soft dark card with a rounded mint ridge on the left. */
  Ridge = "Ridge",
  /** Near-transparent fill under a faint mint haze at the top-left. */
  Haze = "Haze",
}

/** How a message says "we're talking about THIS one" after a status-tag click
 *  lands the user on the halt question (or the chair's conclusion). Every
 *  member is a different MECHANISM of catching the eye, not a different weight
 *  of one idea; all of them are inset, pointer-inert overlays that settle
 *  inside ~1.2s and then hold. Rendered by
 *  `chat/ChatMessageItem/chatMessageIndication.tsx`. */
export enum MessageIndicationVariant {
  /** The shipped placeholder — mint left edge under a wash, faded in and held. */
  Classic = "Classic",
  /** A light runs the row's left rail once and leaves the rail lit. */
  Sweep = "Sweep",
  /** The row briefly gains a glass plate that swells in and settles. */
  Breath = "Breath",
  /** Everything else dims for a beat, then releases to a whisper. */
  Spotlight = "Spotlight",
  /** An edgeless mint glow rises behind the row, peaks, and settles. */
  Bloom = "Bloom",
  /** A mint rule draws itself left to right under the row and stays. */
  Underscore = "Underscore",
}

/** The Quietline children whose spine + sticky author row are drawn by the
 *  message row rather than the bubble itself. */
export function isQuietlineSpineChatBubbleVariant(
  variant: ChatBubbleVariant,
): boolean {
  return (
    variant === ChatBubbleVariant.QuietlineFade ||
    variant === ChatBubbleVariant.QuietlineFadeWash ||
    variant === ChatBubbleVariant.QuietlineFadeClip ||
    variant === ChatBubbleVariant.QuietlineMint ||
    variant === ChatBubbleVariant.QuietlineMember ||
    variant === ChatBubbleVariant.QuietlineMemberFade ||
    variant === ChatBubbleVariant.QuietlineMemberMuted ||
    variant === ChatBubbleVariant.QuietlineGlass
  );
}

/** Quietline/Fade family — fading spine paint + mask-image dissolve under the
 *  sticky author row. */
export function isQuietlineFadeChatBubbleVariant(
  variant: ChatBubbleVariant,
): boolean {
  return (
    variant === ChatBubbleVariant.QuietlineFade ||
    variant === ChatBubbleVariant.QuietlineFadeWash ||
    variant === ChatBubbleVariant.QuietlineFadeClip
  );
}

/** The share/settings/privacy icon cluster is baked as Bare/Underlined/Ghost
 *  (chromeless circular icons + mint underline hover) in SessionPanelControlsRow. */

/** The content-version author row (avatar + name/role + date). The non-
 *  Classic members reuse the member pill's icon wrapper for the avatar. */
export enum VersionAuthorRowVariant {
  /** The shipped row — plain member icon + name/role stack. */
  Classic = "Classic",
  /** Pill-frame avatar, name/role stack kept. */
  Badge = "Badge",
  /** Pill-frame avatar with "name · role" on one line. */
  Inline = "Inline",
  /** Badge layout seated on a faint hairline plate. */
  Plate = "Plate",
  // Plate children that pull the version summary INTO the plate (so the
  // separate summary peek below is suppressed), each framing it differently.
  /** Summary as a quiet 2-line caption under the author row. */
  PlateBrief = "Plate/Brief",
  /** Summary indented behind a mint left rule. */
  PlateQuote = "Plate/Quote",
  /** Summary in a hairline-topped footer band inside the plate. */
  PlateFooter = "Plate/Footer",
  /** Summary in a collapsible peek seated inside the plate. */
  PlatePeek = "Plate/Peek",
  // Plate/Peek children — the peek body padded and wrapped in quotation
  // marks so the summary reads as a quote.
  /** Padded peek body flanked by inline quotation marks. */
  PlatePeekQuoted = "Plate/Peek/Quoted",
  /** Padded peek body under an oversized ghost opening quote. */
  PlatePeekBlockquote = "Plate/Peek/Blockquote",
  /** The author as a minified member pill (dark pill surface + frame). */
  Chip = "Chip",
}

/** Plate + its summary-carrying children all share the plate surface. */
export function isPlateAuthorRowVariant(
  variant: VersionAuthorRowVariant,
): boolean {
  return (
    variant === VersionAuthorRowVariant.Plate ||
    variant === VersionAuthorRowVariant.PlateBrief ||
    variant === VersionAuthorRowVariant.PlateQuote ||
    variant === VersionAuthorRowVariant.PlateFooter ||
    isPlatePeekAuthorRowVariant(variant)
  );
}

/** Plate/Peek and its quote-styled children render the summary in a Cpeek. */
export function isPlatePeekAuthorRowVariant(
  variant: VersionAuthorRowVariant,
): boolean {
  return (
    variant === VersionAuthorRowVariant.PlatePeek ||
    variant === VersionAuthorRowVariant.PlatePeekQuoted ||
    variant === VersionAuthorRowVariant.PlatePeekBlockquote
  );
}

/** The Plate children that render the summary in-plate (base Plate does not). */
export function isPlateSummaryAuthorRowVariant(
  variant: VersionAuthorRowVariant,
): boolean {
  return (
    isPlateAuthorRowVariant(variant) &&
    variant !== VersionAuthorRowVariant.Plate
  );
}

/** The session's changes-review modal (`Cdiffmodal`).
 *
 *  RATIFIED 2026-08-19: the surface itself is settled — full viewport width
 *  AND height, no card wrapper, painted in the teams-gallery expanded
 *  overlay's dark material (scrim + `MYSTERIOUS_BG` + mint corner glow). What
 *  is still open is what that stage is USED for, which is this axis: every
 *  member spends the reclaimed width differently. */
export enum DiffModalLayoutVariant {
  /** One centred reading measure under a full-width masthead. Height only. */
  Flow = "Flow",
  /** Before | After as two documents side by side, each its own scroller. */
  Split = "Split",
  /** A navigable index of every change on the left, the document right. */
  Ledger = "Ledger",
  /** Editorial proof — removals set as marginalia beside the surviving spine. */
  Galley = "Galley",
  /** The meta furniture as a left masthead column, the document on the stage. */
  Theatre = "Theatre",
}

/** How one added/removed hunk is PAINTED, inside whichever layout is showing.
 *  The shipped rounded plate was drawn for a 480px card; at full width it is a
 *  different design problem, so the paint is its own question. */
export enum DiffChangeEmphasisVariant {
  /** The shipped rounded tinted plate. */
  Blocks = "Blocks",
  /** No fill — a coloured inset rule and a ± sigil in a left gutter. */
  Gutter = "Gutter",
  /** Edge-to-edge tint band, no radius, hairline seams top and bottom. */
  Wash = "Wash",
  /** Near-black plate with a coloured edge — a card filed on the stage. */
  Slab = "Slab",
}
/** The expanded content modal's document header is baked as the locked
 *  Pinned/Glass/Reveal/Compact treatment (ContentDocumentCard) — pinned in
 *  the modal's out-of-scroll slot, chromeless at rest, white glass + a
 *  scaled-down title once the content scrolls. */

/** The markdown table's sticky header-row surface (tweak f3ef2va): the
 *  shipped near-transparent tint let scrolled body text bleed through the
 *  header, so every non-Classic member paints an occluding surface at the
 *  ROW level (the sticky element itself — where backdrop-filter reliably
 *  composites) instead of per cell. */
export enum TableHeaderSurfaceVariant {
  /** The shipped whisper mint tint + blur(4px) (bleeds under the header). */
  Classic = "Classic",
  /** Mint tint over an opaque near-black plate — occlusion by opacity. */
  Plate = "Plate",
  /** Translucent dark frost under a strong blur — a real glass read. */
  Frost = "Frost",
  /** Fully opaque flat near-black fill, no blur at all. */
  Solid = "Solid",
  // Extra surface variations (tweak 5vohv5s — "try more variations").
  /** Dense near-black ink slab — max occlusion, no blur. */
  Ink = "Ink",
  /** Soft charcoal veil with a medium blur. */
  Obscure = "Obscure",
  /** Whisper-dark translucent film — lighter than Frost. */
  Veil = "Veil",
  /** Opaque fill with only a bottom hairline (no blur / wash). */
  Hairline = "Hairline",
  /** Stronger mint wash over an opaque plate. */
  MintWash = "Mint wash",
  /** Cool porcelain grey plate — no mint tint. */
  Porcelain = "Porcelain",
  // Opus companions (one per Grok surface) — same dark/mint occluding family,
  // every member opaque or strong-frost so scrolled body text can't bleed.
  /** Deeper pure onyx slab with a top catch-light (companion to Ink). */
  Onyx = "Onyx",
  /** Charcoal glass under a heavier blur + base hairline (companion to Obscure). */
  Fog = "Fog",
  /** Darker top-lit gradient veil with a base hairline (companion to Veil). */
  Shroud = "Shroud",
  /** Opaque plate framed by a mint top + white bottom hairline (companion to Hairline). */
  Underscore = "Underscore",
  /** Stronger cool mint wash over a deep plate + mint base (companion to Mint wash). */
  Verdigris = "Verdigris",
  /** Cool neutral slate-grey plate, no mint tint (companion to Porcelain). */
  Slate = "Slate",
  // Premium Ethereal Glass set (tweak iql20rs — "try something that'll
  // actually look good") — every member opaque or strong frost so scrolled
  // body text stays occluded under the sticky header.
  /** Nested double-bezel: outer hairline + inner plate. */
  Doppelrand = "Doppelrand",
  /** Soft mint crown wash over a deep plate + catch-light. */
  Lumen = "Lumen",
  /** Triple mint/white hairlines over an opaque plate. */
  Filigree = "Filigree",
  /** Deep void plate with a subtle top vignette. */
  Eclipse = "Eclipse",
  /** Mint left/top prism edge over onyx. */
  PrismEdge = "PrismEdge",
}

/** The unassigned session-member chip is baked as the locked Ghost glass/Slate
 *  ring treatment: ghost-glass frost over the SurfaceRoot (sessionMemberItem
 *  constants), a neutral hairline-ring empty seat (SessionMemberIcon), and the
 *  quieter 13px faded seat label (SessionMemberNameRow). */

/** The session member pill's surface (background + accent) treatment. */
export enum MemberPillSurfaceVariant {
  /** The shipped member-card material — accent top wash over near-black. */
  Classic = "Classic",
  /** Deeper near-black under a stronger accent crown glow. */
  Nightfall = "Nightfall",
  /** A full accent wash with an accent hairline ring. */
  Tintwash = "Tintwash",
  /** Bright white glass with an accent underglow at the base. */
  Duskglass = "Duskglass",
  // Duskglass children — glass kept, member accent drives tint/shade
  // (refinement: "create variants of the glass with the member accent
  // tinting / shading").
  /** Whisper accent wash through the glass. */
  DuskglassMist = "Duskglass/Mist",
  /** Stronger accent veil + deeper underglow. */
  DuskglassVeil = "Duskglass/Veil",
  /** Accent catch-light sheen along the top rim. */
  DuskglassSheen = "Duskglass/Sheen",
  /** Accent bloom from the lower-left corner under the glass. */
  DuskglassBloom = "Duskglass/Bloom",
  /** Full glass tinted with the member accent. */
  DuskglassTint = "Duskglass/Tint",
  /** Quiet body drawn by a 1.5px accent ring + soft outer glow. */
  Ring = "Ring",
  /** An accent ember bleeding in from the left edge. */
  Ember = "Ember",
}

/** Duskglass + its accent-tint children share the glass plate language. */
export function isDuskglassMemberPillSurfaceVariant(
  variant: MemberPillSurfaceVariant,
): boolean {
  return (
    variant === MemberPillSurfaceVariant.Duskglass ||
    variant === MemberPillSurfaceVariant.DuskglassMist ||
    variant === MemberPillSurfaceVariant.DuskglassVeil ||
    variant === MemberPillSurfaceVariant.DuskglassSheen ||
    variant === MemberPillSurfaceVariant.DuskglassBloom ||
    variant === MemberPillSurfaceVariant.DuskglassTint
  );
}

/** The lone chat image attachment is baked as the locked Bloom/Porthole
 *  treatment (chatImageAttachmentFrames.ts): contained fit over a blurred
 *  bloom of itself, inside a double-ring porthole tray, with the redesigned
 *  "Attached files" tag on file-only messages. */

/** Paren lists (`a) b) c)`) are baked as the locked Lettered treatment: the
 *  author's letters stay the ordered-list marker (ParenListBlock). */

/** Inline markdown inherited-span tertiary text treatments
 *  (renderInheritedSpanTertiaryText probe). */
export enum InheritedSpanTertiaryTextVariant {
  /** Shipped inherit-only typography contract. */
  Classic = "Classic",
  /** Quieter opacity wash. */
  Whisper = "Whisper",
  /** Carved inset text-shadow. */
  Etched = "Etched",
  /** Mint hairline underline filament. */
  MintFilament = "Mint filament",
  /** Soft dew film pill behind tokens. */
  DewFilm = "Dew film",
  /** Porcelain double-bezel inline shell. */
  Porcelain = "Porcelain",
  /** Editorial letter-track + feature settings. */
  EditorialTrack = "Editorial track",
}

/** The session team chip's CONTAINER surface is baked as the locked Duskfilm
 *  treatment (Csessionteamchip) — soft white glass with a mint floor bloom
 *  on the Cbutton shell only, never the image/icon frame (issue gslgx7g). */

/** The "member is working" ambience is baked as the locked Accent sparks/
 *  Halo/Fine loft treatment (SessionMemberWorkingCue): fine rising accent
 *  motes lofting past the pill crown under a breathing icon halo. It replaces
 *  the legacy outer boxShadow halo + status-dot blink whenever a member is
 *  working. */

/** The resolving map surface is baked as the locked Radar roll treatment
 *  (MapResolvingLoader): a sweeping radar disc with the location names
 *  ledgered beside it, pinging in sweep rhythm. The names load inside the
 *  stage, so MapSurface suppresses the standalone MapLocationList while
 *  geocoding runs. */

/** The compact 317 map tile is baked as the locked Grid bleed/Pocket globe/
 *  Feather treatment (MapCompactPreview): a level grid field feathered on all
 *  four sides, the pocket meridian orb tucked at the lower-right rim, and the
 *  numbered place chips over the composition. */

/** The expanded map surface is baked as the locked Atlas stage/Terrace
 *  treatment (MapSurface): a full-bleed stage with the location list docked
 *  as a glass rail of two-line terrace cards, and teardrop pin dots. */

/** Retired axes, all baked into their components and no longer switchable:
 *  the map's cartography (names/roads/waters), its POI popup and rail cards,
 *  its resolving loader, the dev force-resolving toggle, and the session
 *  loading text. The dev tweaks panel they fed is gone with them. */

/** The inline reference pill is baked as the locked Flow tight treatment
 *  (creferencePillContentModel): the label caps at 24 chars (`..`) and flows
 *  as real inline text, breaking where the line ends — never after the first
 *  word. The leading seal carries the icon alone. */

/** A decision-role product attachment is baked as the locked Fork solo
 *  treatment (ChatProductAttachmentButton): the fork glyph alone, in the type
 *  icon's accent read, replacing the product-type icon rather than appending
 *  a badge beside it. */

/** How the product card announces that a product's FIRST version exists but
 *  carries no content yet — the `round_focus` seed state (`V1`, title
 *  `(empty)`, empty body). Classic is the shipped pair (a `V1` version chip
 *  plus the "In Progress" status label), which reads as a document you can
 *  open when there is nothing there; every other member replaces BOTH with a
 *  single cue that says the draft is still being written.
 *  Rendered by `Cproductcard/ProductCardFirstDraftCue`. */
export enum FirstDraftCueVariant {
  /** The shipped `V1` chip + status label pair. */
  Classic = "Classic",
  /** An emitting mint dot breathing beside the label; no container. */
  Pulse = "Pulse",
  /** A mint highlight travelling through the lettering itself. */
  Sheen = "Sheen",
  /** The system's own per-character loading wave (`Ctext` iterate-fade). */
  Iterate = "Iterate",
  /** The label trailed by three mint dots stepping in sequence. */
  Ellipsis = "Ellipsis",
  /** Uppercase Meta rail behind a breathing mint ridge. */
  Ruled = "Ruled",
  /** The version chip's silhouette emptied — a dashed hairline outline. */
  Ghostchip = "Ghostchip",
  /** A seated dent plate with a mint wash sweeping through it. */
  Fill = "Fill",
  /** A drawn writing tip nudging along its stroke ahead of the label. */
  Nib = "Nib",
}

/** Classic is the shipped chip + status pair, so the cue component stands
 *  down and the meta row keeps its original two children. */
export function isClassicFirstDraftCueVariant(
  variant: FirstDraftCueVariant,
): boolean {
  return variant === FirstDraftCueVariant.Classic;
}

// The main panel stacks its layers (bezel, core, overlay cards, subproduct
// cards) one on top of the other, so they share ONE selected surface. The
// Overview top panel is locked to Smoke (SessionOverviewSurfaceTile).
export const SESSION_PANEL_SURFACE_DEFAULT =
  SessionSurfaceVariant.QuartzContentFadeDissolve;
// Locked pick (chatBubble:Quietline/Fade) — Quietline/Fade is THE default.
export const CHAT_BUBBLE_DEFAULT = ChatBubbleVariant.QuietlineFade;
export const MESSAGE_INDICATION_DEFAULT = MessageIndicationVariant.Classic;
export const VERSION_AUTHOR_ROW_DEFAULT =
  VersionAuthorRowVariant.PlatePeekBlockquote;
export const MEMBER_PILL_SURFACE_DEFAULT =
  MemberPillSurfaceVariant.DuskglassBloom;
export const TABLE_HEADER_SURFACE_DEFAULT = TableHeaderSurfaceVariant.Eclipse;
export const INHERITED_SPAN_TERTIARY_TEXT_DEFAULT =
  InheritedSpanTertiaryTextVariant.Classic;
export const FIRST_DRAFT_CUE_DEFAULT = FirstDraftCueVariant.Pulse;
export const DIFF_MODAL_LAYOUT_DEFAULT = DiffModalLayoutVariant.Flow;
export const DIFF_CHANGE_EMPHASIS_DEFAULT = DiffChangeEmphasisVariant.Blocks;
// Chat message container — gap between body/attachments/footer, and font size.
// Locked v1 tweak-slider values, baked (chatBubbleGap=5, chatBubbleFontSize=14).
export const CHAT_BUBBLE_GAP_DEFAULT_PX = 5;
export const CHAT_BUBBLE_FONT_SIZE_DEFAULT_PX = 14;

// ---------------------------------------------------------------------------
// Chat decrypt reveal speed — a handler-authored slider on the mark's
// `existing` option (the mark registers NO options; the knob scales the
// shipped animation rather than swapping it).
// ---------------------------------------------------------------------------

/** The decrypt-speed mark's name in the "session" tweaks context. */
const CHAT_DECRYPT_SPEED_TWEAKS_MARK = "chatDecryptSpeed";

/** Control ids on the decrypt-speed mark's `existing` option. */
export enum ChatDecryptSpeedControl {
  SpeedMultiplier = "decryptSpeedMultiplier",
}

/** Code default — MIRRORED by the seeded slider value in
 *  `apps/fe/src/app/dev/tweaks/contexts/session.json`. One multiplier scales
 *  the ENTIRE reveal timeline of a new chat message (cipher fill, hold,
 *  decrypt sweep, and the next-message release delay). */
export const CHAT_DECRYPT_SPEED_MULTIPLIER_DEFAULT = 1;

// ---------------------------------------------------------------------------
// Tweaks v2 registration — the still-open session design questions
// ---------------------------------------------------------------------------

/** The tweaks2 context every session-screen mark registers under. */
const SESSION_TWEAKS_CONTEXT_NAME = "session";

/** Registered option lists — every variant of a still-open question except its
 *  code default. The default is the v2 mechanism's implicit "existing" option
 *  (the fall-through every consumer renders on all-false booleans), so
 *  registering it would duplicate the panel's existing-rendering row. */
const FIRST_DRAFT_CUE_TWEAK_OPTIONS = Object.values(FirstDraftCueVariant).filter(
  (variant) => variant !== FIRST_DRAFT_CUE_DEFAULT,
);
const MESSAGE_INDICATION_TWEAK_OPTIONS = Object.values(
  MessageIndicationVariant,
).filter((variant) => variant !== MESSAGE_INDICATION_DEFAULT);
const DIFF_MODAL_LAYOUT_TWEAK_OPTIONS = Object.values(
  DiffModalLayoutVariant,
).filter((variant) => variant !== DIFF_MODAL_LAYOUT_DEFAULT);
const DIFF_CHANGE_EMPHASIS_TWEAK_OPTIONS = Object.values(
  DiffChangeEmphasisVariant,
).filter((variant) => variant !== DIFF_CHANGE_EMPHASIS_DEFAULT);

/** The v2 registration spec — one mark per still-open v1 question (the mark
 *  names are the v1 group keys, kept verbatim for provenance). Registering
 *  through `useTweaks` creates the marks in
 *  `app/dev/tweaks/contexts/session.json`; the /dev/tweaks page and the
 *  handler CLI drive selections from there. */
const SESSION_TWEAKS_SPEC = {
  productCardFirstDraftCue: FIRST_DRAFT_CUE_TWEAK_OPTIONS,
  chatMessageIndication: MESSAGE_INDICATION_TWEAK_OPTIONS,
  diffModalLayout: DIFF_MODAL_LAYOUT_TWEAK_OPTIONS,
  diffChangeEmphasis: DIFF_CHANGE_EMPHASIS_TWEAK_OPTIONS,
  // No options on purpose: only the `existing` row, carrying the speed slider.
  [CHAT_DECRYPT_SPEED_TWEAKS_MARK]: [],
};

/** The decrypt-speed slider's current value, or the code default when the
 *  control (or the whole dev store) is absent — production and provider-less
 *  mounts resolve to 1. Non-positive stored values are rejected the same way. */
function readChatDecryptSpeedMultiplier(
  controlValue: TweakControlValueReader,
): number {
  const value = controlValue({
    mark: CHAT_DECRYPT_SPEED_TWEAKS_MARK,
    option: TweakOptionSentinel.Existing,
    control: ChatDecryptSpeedControl.SpeedMultiplier,
  });
  return typeof value === "number" && value > 0
    ? value
    : CHAT_DECRYPT_SPEED_MULTIPLIER_DEFAULT;
}

interface SessionTweaksContextValue {
  panelSurfaceVariant: SessionSurfaceVariant;
  chatBubbleVariant: ChatBubbleVariant;
  /** Uniform scale on the chat decrypt reveal timeline; 1 = shipped speed. */
  chatDecryptSpeedMultiplier: number;
  messageIndicationVariant: MessageIndicationVariant;
  versionAuthorRowVariant: VersionAuthorRowVariant;
  memberPillSurfaceVariant: MemberPillSurfaceVariant;
  tableHeaderSurfaceVariant: TableHeaderSurfaceVariant;
  inheritedSpanTertiaryTextVariant: InheritedSpanTertiaryTextVariant;
  diffModalLayoutVariant: DiffModalLayoutVariant;
  diffChangeEmphasisVariant: DiffChangeEmphasisVariant;
  firstDraftCueVariant: FirstDraftCueVariant;
}

const SESSION_TWEAKS_CONTEXT_FALLBACK: SessionTweaksContextValue = {
  panelSurfaceVariant: SESSION_PANEL_SURFACE_DEFAULT,
  chatBubbleVariant: CHAT_BUBBLE_DEFAULT,
  chatDecryptSpeedMultiplier: CHAT_DECRYPT_SPEED_MULTIPLIER_DEFAULT,
  messageIndicationVariant: MESSAGE_INDICATION_DEFAULT,
  versionAuthorRowVariant: VERSION_AUTHOR_ROW_DEFAULT,
  memberPillSurfaceVariant: MEMBER_PILL_SURFACE_DEFAULT,
  tableHeaderSurfaceVariant: TABLE_HEADER_SURFACE_DEFAULT,
  inheritedSpanTertiaryTextVariant: INHERITED_SPAN_TERTIARY_TEXT_DEFAULT,
  diffModalLayoutVariant: DIFF_MODAL_LAYOUT_DEFAULT,
  diffChangeEmphasisVariant: DIFF_CHANGE_EMPHASIS_DEFAULT,
  firstDraftCueVariant: FIRST_DRAFT_CUE_DEFAULT,
};

const SessionTweaksContext = createContext<SessionTweaksContextValue | null>(
  null,
);

/**
 * The session screen's design-axis provider, wired to tweaks v2.
 *
 * The four still-open axes resolve from the "session" tweaks context's
 * booleans (a /dev/tweaks preview or pick drives exactly one true); every
 * retired axis is its baked default. With no selection, in production, or
 * with the tweaks provider absent, all booleans are false and every axis
 * renders its default — the same rendering a production leak would show.
 */
export function SessionTweaksProvider({ children }: { children: ReactNode }) {
  const tweaks = useTweaks(SESSION_TWEAKS_CONTEXT_NAME, SESSION_TWEAKS_SPEC);
  const value = useMemo<SessionTweaksContextValue>(
    () => ({
      panelSurfaceVariant: SESSION_PANEL_SURFACE_DEFAULT,
      chatBubbleVariant: CHAT_BUBBLE_DEFAULT,
      chatDecryptSpeedMultiplier: readChatDecryptSpeedMultiplier(
        tweaks.controlValue,
      ),
      messageIndicationVariant: resolveTweakedVariant({
        tweaks,
        options: MESSAGE_INDICATION_TWEAK_OPTIONS,
        fallback: MESSAGE_INDICATION_DEFAULT,
      }),
      versionAuthorRowVariant: VERSION_AUTHOR_ROW_DEFAULT,
      memberPillSurfaceVariant: MEMBER_PILL_SURFACE_DEFAULT,
      tableHeaderSurfaceVariant: TABLE_HEADER_SURFACE_DEFAULT,
      inheritedSpanTertiaryTextVariant: INHERITED_SPAN_TERTIARY_TEXT_DEFAULT,
      diffModalLayoutVariant: resolveTweakedVariant({
        tweaks,
        options: DIFF_MODAL_LAYOUT_TWEAK_OPTIONS,
        fallback: DIFF_MODAL_LAYOUT_DEFAULT,
      }),
      diffChangeEmphasisVariant: resolveTweakedVariant({
        tweaks,
        options: DIFF_CHANGE_EMPHASIS_TWEAK_OPTIONS,
        fallback: DIFF_CHANGE_EMPHASIS_DEFAULT,
      }),
      firstDraftCueVariant: resolveTweakedVariant({
        tweaks,
        options: FIRST_DRAFT_CUE_TWEAK_OPTIONS,
        fallback: FIRST_DRAFT_CUE_DEFAULT,
      }),
    }),
    [tweaks],
  );

  return (
    <SessionTweaksContext.Provider value={value}>
      {children}
    </SessionTweaksContext.Provider>
  );
}

export function useSessionTweaks(): SessionTweaksContextValue {
  return useContext(SessionTweaksContext) ?? SESSION_TWEAKS_CONTEXT_FALLBACK;
}
